import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const TEAL = "#00E5FF";
const MAX_TRIES = 8;

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("/cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");
  const [detail, setDetail] = useState("");
  const [attempt, setAttempt] = useState(0);
  const tries = useRef(0);

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setDetail("We didn't receive a payment reference.");
      setState("error");
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setDetail("This payment did not complete.");
          setState("error");
          return;
        }
        // still "pending" — keep polling
      } catch (err) {
        const status = err.response?.status;
        // Definitive failures: don't keep pretending it's pending.
        if (status === 404) { setDetail("We couldn't find this payment on your account."); setState("error"); return; }
        if (status === 401 || status === 403) { setDetail("Your session expired. Please sign in again and check your plan."); setState("error"); return; }
        // Transient (network / 5xx): fall through and retry, then surface an error.
        if (tries.current + 1 >= MAX_TRIES) {
          setDetail("We couldn't reach the payment service. If you were charged, it will still be applied.");
          setState("error");
          return;
        }
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) { setState("slow"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, location.search, attempt]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#07090E] px-6">
      <div className="max-w-sm w-full text-center space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="payment-result" data-state={state}>
        {state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: TEAL }} strokeWidth={1.5} />
            <p className="text-slate-400">Confirming your payment&hellip;</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: TEAL }} strokeWidth={1.5} data-testid="payment-success-icon" />
            <h1 className="text-2xl text-white">You&apos;re all set</h1>
            <p className="text-sm text-slate-400">Thank you. Your plan and credits are ready &mdash; may it serve your time in the Word.</p>
            <Button data-testid="payment-continue" onClick={() => navigate("/billing")} className="rounded-full text-white" style={{ backgroundColor: TEAL }}>
              Back to my plan
            </Button>
          </>
        )}
        {state === "slow" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto" style={{ color: TEAL }} strokeWidth={1.5} />
            <h1 className="text-2xl text-white">Almost there</h1>
            <p className="text-sm text-slate-400">Your payment is processing. Your plan will update shortly &mdash; check back on your plan page in a moment.</p>
            <Button data-testid="payment-continue" onClick={() => navigate("/billing")} className="rounded-full text-white" style={{ backgroundColor: TEAL }}>
              Back to my plan
            </Button>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-slate-500" strokeWidth={1.5} />
            <h1 className="text-2xl text-white">Payment cancelled</h1>
            <p className="text-sm text-slate-400">No charge was made. You can come back whenever you&apos;re ready.</p>
            <Button data-testid="payment-continue" onClick={() => navigate("/billing")} variant="outline" className="rounded-full border-[#29344D] text-slate-400">
              Back to my plan
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-300" strokeWidth={1.5} />
            <h1 className="text-2xl text-white">Something went wrong</h1>
            <p className="text-sm text-slate-400" data-testid="payment-error-detail">{detail || "We couldn't confirm this payment."}</p>
            <div className="flex gap-2 justify-center">
              <Button
                data-testid="payment-retry"
                onClick={() => { tries.current = 0; setDetail(""); setState("polling"); setAttempt((a) => a + 1); }}
                className="rounded-full text-white"
                style={{ backgroundColor: TEAL }}
              >
                Try again
              </Button>
              <Button data-testid="payment-continue" onClick={() => navigate("/billing")} variant="outline" className="rounded-full border-[#29344D] text-slate-400">
                Back to my plan
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
